import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  Plus,
  Search,
  Mail,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Copy,
  Play,
  Pause,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  draft: 'badge-gray',
  scheduled: 'badge-info',
  sending: 'badge-warning',
  sent: 'badge-success',
  paused: 'badge-warning',
  cancelled: 'badge-danger'
};

const statusIcons = {
  draft: Clock,
  scheduled: Clock,
  sending: Send,
  sent: CheckCircle,
  paused: Pause,
  cancelled: XCircle
};

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    templateId: '',
    listId: '',
    scheduledAt: ''
  });

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || ''
  });
  // Real templates and lists for the create form (it used to show dummy options).
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [searchParams]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [t, l] = await Promise.all([adminApi.getEmailTemplates(), adminApi.getMarketingLists()]);
        setTemplates(t.data?.data || []);
        setLists(l.data?.data || []);
      } catch (err) {
        console.error('Failed to load templates/lists:', err);
      }
    };
    loadOptions();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = {
        page: searchParams.get('page') || 1,
        search: searchParams.get('search') || '',
        status: searchParams.get('status') || '',
        limit: 20
      };

      const response = await adminApi.getCampaigns(params);
      setCampaigns(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load campaigns');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.getCampaignStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to load campaign stats:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (filters.search) {
      newParams.set('search', filters.search);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createCampaign(formData);
      toast.success('Campaign created successfully');
      setShowCreateModal(false);
      setFormData({
        name: '',
        subject: '',
        templateId: '',
        listId: '',
        scheduledAt: ''
      });
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to create campaign');
      console.error(err);
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!confirm(`Are you sure you want to delete "${campaign.name}"?`)) return;

    try {
      await adminApi.deleteCampaign(campaign.id);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to delete campaign');
      console.error(err);
    }
  };

  const handleSendCampaign = async (campaign) => {
    if (!confirm(`Are you sure you want to send "${campaign.name}" now?`)) return;

    try {
      await adminApi.sendCampaign(campaign.id);
      toast.success('Campaign is being sent');
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to send campaign');
      console.error(err);
    }
  };

  const handlePauseCampaign = async (campaign) => {
    try {
      await adminApi.pauseCampaign(campaign.id);
      toast.success('Campaign paused');
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to pause campaign');
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-gray-500 mt-1">Create and manage email campaigns</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          New Campaign
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats?.totalSent || 0}</p>
          <p className="text-sm text-gray-500">Emails Sent</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-100 rounded-lg">
              <Eye className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats?.avgOpenRate || 0}%</p>
          <p className="text-sm text-gray-500">Avg. Open Rate</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats?.avgClickRate || 0}%</p>
          <p className="text-sm text-gray-500">Avg. Click Rate</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">{stats?.scheduled || 0}</p>
          <p className="text-sm text-gray-500">Scheduled</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="input pl-10"
              />
            </div>
          </form>

          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sending">Sending</option>
              <option value="sent">Sent</option>
              <option value="paused">Paused</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header">Campaign</th>
                <th className="table-header">Status</th>
                <th className="table-header">Recipients</th>
                <th className="table-header">Opens</th>
                <th className="table-header">Clicks</th>
                <th className="table-header">Sent/Scheduled</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : campaigns.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No campaigns found
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => {
                  const StatusIcon = statusIcons[campaign.status] || Clock;
                  return (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="p-2 bg-primary-100 rounded-lg mr-3">
                            <Mail className="w-4 h-4 text-primary-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{campaign.name}</p>
                            <p className="text-xs text-gray-500 truncate max-w-xs">
                              {campaign.subject}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={statusColors[campaign.status] || 'badge-gray'}>
                          <StatusIcon className="w-3 h-3 mr-1 inline" />
                          {campaign.status}
                        </span>
                      </td>
                      <td className="table-cell text-gray-900">
                        {campaign.recipientCount || 0}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <span className="text-gray-900">{campaign.openCount || 0}</span>
                          {campaign.status === 'sent' && campaign.recipientCount > 0 && (
                            <span className="text-xs text-gray-500 ml-1">
                              ({Math.round((campaign.openCount / campaign.recipientCount) * 100)}%)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <span className="text-gray-900">{campaign.clickCount || 0}</span>
                          {campaign.status === 'sent' && campaign.openCount > 0 && (
                            <span className="text-xs text-gray-500 ml-1">
                              ({Math.round((campaign.clickCount / campaign.openCount) * 100)}%)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell text-gray-500">
                        {campaign.sentAt
                          ? new Date(campaign.sentAt).toLocaleDateString()
                          : campaign.scheduledAt
                          ? new Date(campaign.scheduledAt).toLocaleDateString()
                          : 'Not scheduled'}
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {campaign.status === 'draft' && (
                            <button
                              onClick={() => handleSendCampaign(campaign)}
                              className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                              title="Send Now"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                          {campaign.status === 'sending' && (
                            <button
                              onClick={() => handlePauseCampaign(campaign)}
                              className="p-2 text-gray-400 hover:text-orange-600 rounded-lg hover:bg-orange-50"
                              title="Pause"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                            title="View Stats"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </button>
                          {campaign.status === 'draft' && (
                            <button
                              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCampaign(campaign)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 20 + 1} to{' '}
              {Math.min(pagination.page * 20, pagination.total)} of {pagination.total} campaigns
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Campaign</h3>
            </div>

            <form onSubmit={handleCreateCampaign} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  placeholder="e.g., January Newsletter"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="input"
                  placeholder="e.g., Your monthly update from Practice Manager Pro"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  value={formData.templateId}
                  onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No templates yet. Create one under Marketing → Templates.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient List
                </label>
                <select
                  value={formData.listId}
                  onChange={(e) => setFormData({ ...formData, listId: e.target.value })}
                  className="input"
                >
                  <option value="">All subscribed contacts</option>
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}{typeof l.contactCount === 'number' ? ` (${l.contactCount})` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduledAt}
                  onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to save as draft
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignsPage;
